"use client"

import { useTheme } from "next-themes"
import { Toaster as Sonner, type ToasterProps } from "sonner"
import {
  CircleCheckIcon,
  InfoIcon,
  TriangleAlertIcon,
  OctagonXIcon,
  Loader2Icon,
} from "lucide-react"
import { buttonVariants } from "@/primitives/button"
import { cn } from "@/primitives/utils"

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme()

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      // beacon: bottom-right, 8px apart, three at most. The offset is from the viewport, and the
      // inset panel stands 8px in from it, so 24 keeps a 16px gutter inside the panel's corner.
      // Each toast is a floating surface: the popover fill and shadow, UI text, a medium title
      // over a meta description, an outline action, and an icon in its state's colour.
      className="toaster group font-sans!"
      position="bottom-right"
      offset={24}
      gap={8}
      visibleToasts={3}
      icons={{
        success: <CircleCheckIcon className="size-4 text-success" />,
        info: <InfoIcon className="size-4 text-brand-text" />,
        warning: <TriangleAlertIcon className="size-4 text-warning" />,
        error: <OctagonXIcon className="size-4 text-destructive" />,
        loading: <Loader2Icon className="size-4 animate-spin text-subtle-foreground" />,
      }}
      style={
        {
          "--normal-bg": "var(--popover)",
          "--normal-text": "var(--popover-foreground)",
          "--normal-border": "var(--border-subtle)",
          "--border-radius": "var(--radius-lg)",
        } as React.CSSProperties
      }
      // Sonner's own stylesheet selects on two attributes, so these carry `!` to win over it.
      toastOptions={{
        classNames: {
          toast:
            "cn-toast gap-2.5! rounded-lg! border-0! bg-popover! px-3.5! py-3! text-ui! text-popover-foreground! shadow-popover!",
          title: "font-medium",
          description: "text-xs text-muted-foreground!",
          actionButton: cn(
            buttonVariants({ variant: "outline", size: "sm" }),
            "h-7! rounded-md! bg-background! px-2.5! text-ui! text-foreground! hover:bg-accent! dark:bg-secondary! dark:hover:bg-selected!",
          ),
          cancelButton: cn(
            buttonVariants({ variant: "ghost", size: "sm" }),
            "h-7! rounded-md! bg-transparent! px-2.5! text-ui! text-muted-foreground! hover:bg-accent! hover:text-foreground!",
          ),
        },
      }}
      {...props}
    />
  )
}

export { Toaster }
